/**
 * OnboardingProvider — IN-2.a
 * ───────────────────────────
 * Persistent state for installer onboarding. Logic-only foundation:
 * no wizard UI, no payout UI, no email sending, no dispatch
 * enforcement, no contractor approval.
 *
 * Owns:
 *   • onboardingInviteTokens[]
 *   • onboardingSessions[]
 *   • onboardingAuditEvents[]  (append-only)
 *   • complianceDocuments[]    (lifted from IN-1.d ComplianceReviewPage)
 *
 * Persistence: localStorage under `koredor.onboarding.v1`.
 *
 * NOTE — no production Contractor mutation here. Provider stores
 * draft state only. IN-2.g will own the bridge to the real
 * contractor record.
 *
 * Anchor: RECONCILIATION.md §16 (IN-2.a)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  type ContractorOnboardingDraft,
  type OnboardingAuditEvent,
  type OnboardingInviteToken,
  type OnboardingSession,
} from "./types";

import {
  consumeInviteToken,
  createInviteToken,
  revokeInviteToken,
  type CreateInviteTokenInput,
} from "./inviteTokens";
import {
  createSessionFromToken,
  updateOnboardingDraft,
} from "./sessions";
import {
  advanceOnboardingSession,
  type OnboardingLifecycleTransition,
} from "./advanceOnboardingSession";
import { appendAuditEvent } from "./auditEvents";
import {
  rejectComplianceDocument,
  submitComplianceDocument,
  verifyComplianceDocument,
  type ComplianceDocument,
  type SubmitComplianceDocumentInput,
} from "@/data/contractor/compliance/complianceDocuments";
import {
  buildLedgerInputForOnboarding,
  type OnboardingDocumentUploadInput,
} from "./documents";
import {
  signOnboardingAgreement,
  type SignOnboardingAgreementInput,
} from "./onboardingAgreements";
import {
  setOnboardingPayout,
  type SetOnboardingPayoutInput,
} from "./payout";
import {
  getAdminApprovalReadiness,
  getOnboardingSubmitReadiness,
  type OnboardingSubmitReadiness,
} from "./submitReadiness";
import { hasAnyObserverIdentity } from "@/data/observerMode/observerSession";
import { supabase } from "@/lib/supabase";
import {
  getOnboardingBackend,
  type OnboardingBackend,
  type OnboardingSnapshot,
} from "./backend";
import { SupabaseOnboardingStore } from "./backend/SupabaseOnboardingStore";
import {
  OnboardingSaveStatusContext,
  IDLE_SAVE_STATUS,
  type OnboardingSaveStatus,
} from "./saveStatus";
import { useDeferOnboardingProviderForCurrentRoute } from "@/lib/routeScope";

// ── Persisted shape (legacy alias kept for export stability) ──────
type PersistedState = OnboardingSnapshot;


// ── Context API ────────────────────────────────────────────────────
export interface OnboardingContextValue {
  inviteTokens: OnboardingInviteToken[];
  sessions: OnboardingSession[];
  auditEvents: OnboardingAuditEvent[];
  complianceDocuments: ComplianceDocument[];

  createInviteToken: (
    input: CreateInviteTokenInput,
    actor: string,
  ) => OnboardingInviteToken;
  revokeInviteToken: (tokenId: string, actor: string, reason: string) => void;
  consumeInviteToken: (
    tokenString: string,
    consumedBy?: string,
  ) => { token: OnboardingInviteToken; session: OnboardingSession };

  updateOnboardingDraft: (
    sessionId: string,
    patch: Partial<ContractorOnboardingDraft>,
    actor: string,
  ) => void;
  completeOnboardingStep: (
    sessionId: string,
    stepKey: string,
    actor: string,
    nextStep?: string,
  ) => void;
  submitOnboardingSession: (sessionId: string, actor: string) => void;
  requestReupload: (
    sessionId: string,
    stepKeys: string[],
    reason: string,
    actor: string,
  ) => void;

  // Document ledger (lifted from IN-1.d local state).
  submitComplianceDocument: (
    input: SubmitComplianceDocumentInput,
    actor: string,
  ) => ComplianceDocument;
  verifyComplianceDocument: (documentId: string, actor: string) => void;
  rejectComplianceDocument: (
    documentId: string,
    actor: string,
    reason: string,
  ) => void;

  // IN-2.c — onboarding wizard actions.
  submitOnboardingDocument: (
    sessionId: string,
    input: OnboardingDocumentUploadInput,
    actor: string,
  ) => ComplianceDocument;
  signOnboardingAgreement: (
    sessionId: string,
    input: SignOnboardingAgreementInput,
    actor: string,
  ) => void;

  // IN-2.d — payout + submit-readiness + gated finalize.
  setOnboardingPayout: (
    sessionId: string,
    input: SetOnboardingPayoutInput,
    actor: string,
  ) => void;
  getOnboardingSubmitReadiness: (
    sessionId: string,
  ) => OnboardingSubmitReadiness;
  finalizeOnboardingSubmit: (sessionId: string, actor: string) => void;

  // IN-2.g — admin handoff actions.
  getAdminApprovalReadiness: (sessionId: string) => OnboardingSubmitReadiness;
  /**
   * Approves a submitted onboarding session.
   *
   * Returns an optional `contractorPatch` IF the invite was bound to a
   * real contractor id (not a synthetic `onboarding:*` id). The caller
   * (admin surface) is responsible for applying it to
   * `AppDataContext.contractors`. Provider does not own the contractors
   * slice — this preserves the IN-2 "no production contractor
   * activation from the provider" rule while still letting the
   * documented IN-2.g `vettingStatus → "approved"` mutation flow.
   *
   * Critically, this never writes `Contractor.onboardingCompletedAt`
   * (P5 portal-tour collision-safe).
   */
  approveOnboarding: (
    sessionId: string,
    actor: string,
    reason: string,
  ) =>
    | {
        sessionId: string;
        contractorPatch: { contractorId: string; vettingStatus: "approved" } | null;
      };
  rejectOnboarding: (
    sessionId: string,
    actor: string,
    reason: string,
  ) => void;
  archiveOnboardingEntry: (
    ref: { sessionId?: string; tokenId?: string },
    actor: string,
    reason: string,
  ) => Promise<OnboardingAccessDeactivationResult>;
  deleteOnboardingEntry: (
    ref: { sessionId?: string; tokenId?: string },
    actor: string,
    reason: string,
  ) => Promise<OnboardingAccessDeactivationResult>;
  /**
   * IN-2.g — admin requests a reupload. Wraps the existing
   * `requestReupload` writer with the same audit + state machine.
   * Renamed at the public surface so admin callers don't accidentally
   * collide with the invitee-side `requestReupload` action.
   */
  requestOnboardingReupload: (
    sessionId: string,
    stepKeys: string[],
    reason: string,
    actor: string,
  ) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined,
);

/**
 * IN-2.h — Observer-mode write guard.
 * Every writer in this provider routes through this helper. While any
 * observer identity is set in sessionStorage, all onboarding /
 * compliance / admin writes throw before touching state.
 */
function assertNotObserving(op: string): void {
  if (hasAnyObserverIdentity()) {
    throw new Error(
      `OnboardingProvider: ${op} is disabled while observing another account.`,
    );
  }
}

export interface OnboardingAccessDeactivationResult {
  crewMembershipsSuspended: number;
  profilesDeactivated: number;
  warnings: string[];
}

const EMPTY_ACCESS_DEACTIVATION_RESULT: OnboardingAccessDeactivationResult = {
  crewMembershipsSuspended: 0,
  profilesDeactivated: 0,
  warnings: [],
};

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(values.map((value) => String(value ?? "").trim()).filter(Boolean)),
  );
}

function realContractorId(value: string | null | undefined): string | null {
  const id = String(value ?? "").trim();
  if (!id || id.startsWith("onboarding:")) return null;
  return id;
}

async function deactivateConnectedOnboardingAccess(input: {
  contractorIds: Array<string | null | undefined>;
  profileEmails: Array<string | null | undefined>;
}): Promise<OnboardingAccessDeactivationResult> {
  const contractorIds = uniqueNonEmpty(input.contractorIds.map(realContractorId));
  const profileEmails = uniqueNonEmpty(input.profileEmails.map((email) => email.toLowerCase()));
  const warnings: string[] = [];
  const touchedProfileIds = new Set<string>();
  const touchedCrewUserIds = new Set<string>();
  let crewMembershipsSuspended = 0;

  if (contractorIds.length > 0) {
    const { data: crews, error } = await supabase
      .from("contractor_crews")
      .select("id, user_id, contractor_id, status")
      .in("contractor_id", contractorIds);

    if (error) {
      warnings.push(`contractor crew lookup failed: ${error.message}`);
    } else {
      const activeCrews = ((crews ?? []) as Array<{
        id?: string | null;
        user_id?: string | null;
        contractor_id?: string | null;
        status?: string | null;
      }>).filter((crew) => {
        const status = String(crew.status ?? "").trim();
        return Boolean(crew.id) && status !== "released" && status !== "suspended";
      });
      const crewIds = uniqueNonEmpty(activeCrews.map((crew) => crew.id));
      for (const crew of activeCrews) {
        const userId = String(crew.user_id ?? "").trim();
        if (userId) touchedCrewUserIds.add(userId);
      }

      if (crewIds.length > 0) {
        const { data: suspendedRows, error: updateError } = await supabase
          .from("contractor_crews")
          .update({
            status: "suspended",
            released_at: new Date().toISOString(),
          })
          .in("id", crewIds)
          .select("id");

        if (updateError) {
          warnings.push(`contractor crew suspension failed: ${updateError.message}`);
        } else {
          crewMembershipsSuspended = (suspendedRows ?? []).length;
        }
      }
    }
  }

  if (profileEmails.length > 0) {
    const { data: profilesByEmail, error } = await supabase
      .from("profiles")
      .select("id, auth_user_id, email")
      .in("email", profileEmails);

    if (error) {
      warnings.push(`profile lookup failed: ${error.message}`);
    } else {
      for (const profile of (profilesByEmail ?? []) as Array<{
        id?: string | null;
        auth_user_id?: string | null;
      }>) {
        const id = String(profile.id ?? "").trim();
        const authUserId = String(profile.auth_user_id ?? "").trim();
        if (id) touchedProfileIds.add(id);
        if (id) touchedCrewUserIds.add(id);
        if (authUserId) touchedCrewUserIds.add(authUserId);
      }
    }
  }

  const candidateUserIds = Array.from(touchedCrewUserIds);
  if (candidateUserIds.length > 0) {
    const { data: profileRows, error } = await supabase
      .from("profiles")
      .select("id, auth_user_id")
      .or(`id.in.(${candidateUserIds.join(",")}),auth_user_id.in.(${candidateUserIds.join(",")})`);

    if (error) {
      warnings.push(`profile user lookup failed: ${error.message}`);
    } else {
      for (const profile of (profileRows ?? []) as Array<{
        id?: string | null;
        auth_user_id?: string | null;
      }>) {
        const id = String(profile.id ?? "").trim();
        if (id) touchedProfileIds.add(id);
      }
    }
  }

  const eligibleProfileIds = Array.from(touchedProfileIds);
  if (eligibleProfileIds.length === 0) {
    return {
      ...EMPTY_ACCESS_DEACTIVATION_RESULT,
      crewMembershipsSuspended,
      warnings,
    };
  }

  const identifiersToCheck = Array.from(new Set([...eligibleProfileIds, ...candidateUserIds]));
  const profilesBlockedByOtherCrews = new Set<string>();
  if (identifiersToCheck.length > 0) {
    const { data: otherCrews, error } = await supabase
      .from("contractor_crews")
      .select("user_id, contractor_id, status")
      .in("user_id", identifiersToCheck)
      .eq("status", "active");

    if (error) {
      warnings.push(`other active crew check failed: ${error.message}`);
    } else {
      const targetContractorIds = new Set(contractorIds);
      for (const crew of (otherCrews ?? []) as Array<{
        user_id?: string | null;
        contractor_id?: string | null;
      }>) {
        const userId = String(crew.user_id ?? "").trim();
        const contractorId = String(crew.contractor_id ?? "").trim();
        if (userId && contractorId && !targetContractorIds.has(contractorId)) {
          profilesBlockedByOtherCrews.add(userId);
        }
      }
    }
  }

  const profileIdsToDeactivate = eligibleProfileIds.filter(
    (id) => !profilesBlockedByOtherCrews.has(id),
  );
  if (profileIdsToDeactivate.length === 0) {
    return {
      crewMembershipsSuspended,
      profilesDeactivated: 0,
      warnings,
    };
  }

  const { data: deactivatedProfiles, error } = await supabase
    .from("profiles")
    .update({
      status: "Deactivated",
      updated_at: new Date().toISOString(),
    })
    .in("id", profileIdsToDeactivate)
    .select("id");

  if (error) {
    warnings.push(`profile deactivation failed: ${error.message}`);
    return {
      crewMembershipsSuspended,
      profilesDeactivated: 0,
      warnings,
    };
  }

  return {
    crewMembershipsSuspended,
    profilesDeactivated: (deactivatedProfiles ?? []).length,
    warnings,
  };
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  // IN-3.a-lite — persistence routed through OnboardingBackend seam.
  // Sprint A.2 — per-write monotonic `seq` + cross-tab `storage` listener
  // implementing version-conflict policy: a newer snapshot from another
  // tab REPLACES local state; an older/equal snapshot is REJECTED. We
  // never silently overwrite a more recent write.
  const backendRef = useRef<OnboardingBackend>(getOnboardingBackend());
  const deferHeavyProvider = useDeferOnboardingProviderForCurrentRoute();
  const initial = useRef<PersistedState>(backendRef.current.loadSnapshot());
  const [inviteTokens, setInviteTokens] = useState(initial.current.inviteTokens);
  const [sessions, setSessions] = useState(initial.current.sessions);
  const [auditEvents, setAuditEvents] = useState(initial.current.auditEvents);
  const [complianceDocuments, setComplianceDocuments] = useState(
    initial.current.complianceDocuments,
  );
  const seqRef = useRef<number>(initial.current.seq ?? 0);
  const skipNextSaveRef = useRef<boolean>(false);
  const isFirstSaveRef = useRef<boolean>(true);
  const hasCompletedInitialHydrationRef = useRef<boolean>(
    !(backendRef.current instanceof SupabaseOnboardingStore),
  );
  const [saveStatus, setSaveStatus] =
    useState<OnboardingSaveStatus>(IDLE_SAVE_STATUS);
  const savingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Persist via backend with monotonic `seq` bump.
  // Sprint B.2.1 — also surfaces save-status (idle | saving | saved | error)
  // for the wizard header. No artificial failure simulation: `error` is
  // only set when saveSnapshot throws naturally (e.g. QuotaExceededError).
  useEffect(() => {
    if (!hasCompletedInitialHydrationRef.current) {
      return;
    }
    if (skipNextSaveRef.current) {
      // Hydration from cross-tab storage event — don't re-save.
      skipNextSaveRef.current = false;
      return;
    }
    // First effect run is the mount hydration — don't render "Saving…"
    // for state we just loaded from disk.
    const isMountHydration = isFirstSaveRef.current;
    isFirstSaveRef.current = false;

    if (!isMountHydration) {
      setSaveStatus((prev) => ({
        kind: "saving",
        lastSavedAt: prev.lastSavedAt,
        error: null,
      }));
    }
    try {
      seqRef.current += 1;
      backendRef.current.saveSnapshot({
        v: 1,
        seq: seqRef.current,
        inviteTokens,
        sessions,
        auditEvents,
        complianceDocuments,
      });
      // Debounced flip to `saved` so the "Saving…" indicator is
      // perceivable to users even though localStorage is synchronous.
      if (savingTimerRef.current) clearTimeout(savingTimerRef.current);
      const stampedAt = Date.now();
      savingTimerRef.current = setTimeout(() => {
        setSaveStatus({
          kind: "saved",
          lastSavedAt: stampedAt,
          error: null,
        });
      }, isMountHydration ? 0 : 400);
    } catch (err) {
      setSaveStatus((prev) => ({
        kind: "error",
        lastSavedAt: prev.lastSavedAt,
        error: err instanceof Error ? err.message : "Save failed",
      }));
    }
  }, [inviteTokens, sessions, auditEvents, complianceDocuments]);

  // Sprint A.2 — multi-tab version-conflict listener.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onStorage(e: StorageEvent) {
      // Only react to our key. Re-load via backend so version validation
      // (corrupt/unknown-version quarantine) runs through the same path.
      if (e.key !== "koredor.onboarding.v1") return;
      try {
        const next = backendRef.current.loadSnapshot();
        if ((next.seq ?? 0) <= seqRef.current) {
          // Stale or equal — reject. Our local writes win.
          return;
        }
        skipNextSaveRef.current = true;
        seqRef.current = next.seq ?? 0;
        setInviteTokens(next.inviteTokens);
        setSessions(next.sessions);
        setAuditEvents(next.auditEvents);
        setComplianceDocuments(next.complianceDocuments);
      } catch {
        /* noop */
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // IN-3.b — Supabase async hydration on mount.
  // SupabaseOnboardingStore.loadSnapshot() returns an empty snapshot
  // synchronously (required by the interface). This effect fetches the
  // real data from Supabase and re-initialises React state once it arrives.
  // Only runs when the backend is a SupabaseOnboardingStore instance.
  useEffect(() => {
    if (deferHeavyProvider) return;

    const backend = backendRef.current;
    if (!(backend instanceof SupabaseOnboardingStore)) return;

    let cancelled = false;
    backend.hydrateAsync().then((snapshot) => {
      if (cancelled) return;
      hasCompletedInitialHydrationRef.current = true;
      if ((snapshot.seq ?? 0) < seqRef.current) {
        // Local writes (if any) already happened before hydration returned —
        // don't overwrite them with stale DB data.
        return;
      }
      skipNextSaveRef.current = true;
      seqRef.current = snapshot.seq ?? 0;
      setInviteTokens(snapshot.inviteTokens);
      setSessions(snapshot.sessions);
      setAuditEvents(snapshot.auditEvents);
      setComplianceDocuments(snapshot.complianceDocuments);
    });

    return () => {
      cancelled = true;
    };
     
  }, [deferHeavyProvider]);



  // ── Actions ──────────────────────────────────────────────────────
  const doCreateInviteToken = useCallback(
    (input: CreateInviteTokenInput, actor: string) => {
      assertNotObserving("createInviteToken");
      const { tokens, token } = createInviteToken(inviteTokens, input, actor);
      setInviteTokens(tokens);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "invite_created",
          tokenId: token.id,
          metadata: {
            entityType: token.entityType,
            parentId: token.parentId,
          },
        }),
      );
      return token;
    },
    [inviteTokens],
  );

  const doRevokeInviteToken = useCallback(
    (tokenId: string, actor: string, reason: string) => {
      assertNotObserving("revokeInviteToken");
      const next = revokeInviteToken(inviteTokens, tokenId, actor, reason);
      setInviteTokens(next);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "invite_revoked",
          tokenId,
          reason,
        }),
      );
    },
    [inviteTokens],
  );

  const doConsumeInviteToken = useCallback(
    (tokenString: string, consumedBy?: string) => {
      assertNotObserving("consumeInviteToken");
      const { tokens, token } = consumeInviteToken(
        inviteTokens,
        tokenString,
        consumedBy,
      );
      const { sessions: nextSessions, session } = createSessionFromToken(
        sessions,
        token,
      );
      setInviteTokens(tokens);
      setSessions(nextSessions);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor: consumedBy || "invitee",
          action: "invite_consumed",
          tokenId: token.id,
          sessionId: session.id,
        }),
      );
      return { token, session };
    },
    [inviteTokens, sessions],
  );

  const doUpdateOnboardingDraft = useCallback(
    (
      sessionId: string,
      patch: Partial<ContractorOnboardingDraft>,
      actor: string,
    ) => {
      assertNotObserving("updateOnboardingDraft");
      const next = updateOnboardingDraft(sessions, sessionId, patch, actor);
      setSessions(next);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "draft_updated",
          sessionId,
          metadata: { fields: Object.keys(patch) },
        }),
      );
    },
    [sessions],
  );

  /**
   * Sprint A closure — single internal helper that funnels every
   * lifecycle mutation through `advanceOnboardingSession()`. Provider
   * writers never call sessions.ts state-machine writers directly.
   */
  const runLifecycle = useCallback(
    (transition: OnboardingLifecycleTransition, actor: string) => {
      const result = advanceOnboardingSession(sessions, transition, actor);
      setSessions(result.sessions);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: result.audit.action,
          sessionId: result.audit.sessionId,
          reason: result.audit.reason,
          metadata: result.audit.metadata,
        }),
      );
      return result;
    },
    [sessions],
  );

  const doCompleteOnboardingStep = useCallback(
    (sessionId: string, stepKey: string, actor: string, nextStep?: string) => {
      assertNotObserving("completeOnboardingStep");
      runLifecycle(
        { kind: "complete_step", sessionId, stepKey, nextStep },
        actor,
      );
    },
    [runLifecycle],
  );

  const doSubmitOnboardingSession = useCallback(
    (sessionId: string, actor: string) => {
      assertNotObserving("submitOnboardingSession");
      runLifecycle({ kind: "submit", sessionId }, actor);
    },
    [runLifecycle],
  );

  const doRequestReupload = useCallback(
    (sessionId: string, stepKeys: string[], reason: string, actor: string) => {
      assertNotObserving("requestReupload");
      runLifecycle(
        { kind: "request_reupload", sessionId, stepKeys, reason },
        actor,
      );
    },
    [runLifecycle],
  );



  const doSubmitComplianceDocument = useCallback(
    (input: SubmitComplianceDocumentInput, actor: string) => {
      assertNotObserving("submitComplianceDocument");
      const { ledger, document } = submitComplianceDocument(
        complianceDocuments,
        input,
        actor,
      );
      setComplianceDocuments(ledger);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "document_submitted",
          documentId: document.id,
          metadata: { contractorId: input.contractorId, type: input.type },
        }),
      );
      return document;
    },
    [complianceDocuments],
  );

  const doVerifyComplianceDocument = useCallback(
    (documentId: string, actor: string) => {
      assertNotObserving("verifyComplianceDocument");
      const next = verifyComplianceDocument(complianceDocuments, documentId, actor);
      setComplianceDocuments(next);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "document_verified",
          documentId,
        }),
      );
    },
    [complianceDocuments],
  );

  const doRejectComplianceDocument = useCallback(
    (documentId: string, actor: string, reason: string) => {
      assertNotObserving("rejectComplianceDocument");
      const next = rejectComplianceDocument(
        complianceDocuments,
        documentId,
        actor,
        reason,
      );
      setComplianceDocuments(next);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "document_rejected",
          documentId,
          reason,
        }),
      );
    },
    [complianceDocuments],
  );

  // ── IN-2.c — onboarding wizard actions ─────────────────────────
  const doSubmitOnboardingDocument = useCallback(
    (
      sessionId: string,
      input: OnboardingDocumentUploadInput,
      actor: string,
    ) => {
      assertNotObserving("submitOnboardingDocument");
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) {
        throw new Error(`OnboardingProvider: session ${sessionId} not found.`);
      }
      const ledgerInput = buildLedgerInputForOnboarding(session, input);
      const { ledger, document } = submitComplianceDocument(
        complianceDocuments,
        ledgerInput,
        actor,
      );
      setComplianceDocuments(ledger);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "document_submitted",
          sessionId,
          documentId: document.id,
          metadata: {
            contractorId: ledgerInput.contractorId,
            type: input.type,
            mimeType: input.mimeType,
            fileSize: input.fileSize,
          },
        }),
      );
      return document;
    },
    [sessions, complianceDocuments],
  );

  const doSignOnboardingAgreement = useCallback(
    (
      sessionId: string,
      input: SignOnboardingAgreementInput,
      actor: string,
    ) => {
      assertNotObserving("signOnboardingAgreement");
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) {
        throw new Error(`OnboardingProvider: session ${sessionId} not found.`);
      }
      const nextSession = signOnboardingAgreement(session, input, actor);
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? nextSession : s)),
      );
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "agreement_signed",
          sessionId,
          metadata: { variant: input.variant, version: input.version },
        }),
      );
    },
    [sessions],
  );

  // ── IN-2.d — payout writer + gated submit ──────────────────────
  const doSetOnboardingPayout = useCallback(
    (sessionId: string, input: SetOnboardingPayoutInput, actor: string) => {
      if (hasAnyObserverIdentity()) {
        throw new Error(
          "OnboardingProvider: payout cannot be set while observing another account.",
        );
      }
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) {
        throw new Error(`OnboardingProvider: session ${sessionId} not found.`);
      }
      const nextSession = setOnboardingPayout(session, input, actor);
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? nextSession : s)),
      );
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "payout_set",
          sessionId,
          metadata: {
            type: input.type,
            placeholderVerified: Boolean(input.placeholderVerified),
            hasLast4: Boolean(input.last4),
          },
        }),
      );
    },
    [sessions],
  );

  const doGetOnboardingSubmitReadiness = useCallback(
    (sessionId: string): OnboardingSubmitReadiness => {
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) {
        return {
          ready: false,
          blockers: [`session: ${sessionId} not found`],
          warnings: [],
        };
      }
      return getOnboardingSubmitReadiness(session, complianceDocuments, sessions);
    },
    [sessions, complianceDocuments],
  );

  const doFinalizeOnboardingSubmit = useCallback(
    (sessionId: string, actor: string) => {
      if (hasAnyObserverIdentity()) {
        throw new Error(
          "OnboardingProvider: cannot submit while observing another account.",
        );
      }
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) {
        throw new Error(`OnboardingProvider: session ${sessionId} not found.`);
      }
      if (session.status === "submitted") return;
      const readiness = getOnboardingSubmitReadiness(
        session,
        complianceDocuments,
        sessions,
      );
      if (!readiness.ready) {
        throw new Error(
          `OnboardingProvider: cannot submit — ${readiness.blockers.join("; ")}`,
        );
      }
      doSubmitOnboardingSession(sessionId, actor);
    },
    [sessions, complianceDocuments, doSubmitOnboardingSession],
  );

  // ── IN-2.g — Admin handoff actions ────────────────────────────
  const doGetAdminApprovalReadiness = useCallback(
    (sessionId: string): OnboardingSubmitReadiness => {
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) {
        return {
          ready: false,
          blockers: [`session: ${sessionId} not found`],
          warnings: [],
        };
      }
      return getAdminApprovalReadiness(session, complianceDocuments, sessions);
    },
    [sessions, complianceDocuments],
  );

  const doApproveOnboarding = useCallback(
    (sessionId: string, actor: string, reason: string) => {
      assertNotObserving("approveOnboarding");
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) {
        throw new Error(`OnboardingProvider: session ${sessionId} not found.`);
      }
      const readiness = getAdminApprovalReadiness(session, complianceDocuments, sessions);
      if (!readiness.ready) {
        throw new Error(
          `OnboardingProvider: cannot approve — ${readiness.blockers.join("; ")}`,
        );
      }
      // Route through canonical lifecycle chokepoint. Append entityType
      // to the audit metadata after the fact (helper preserves the
      // base audit row from advanceOnboardingSession).
      const result = advanceOnboardingSession(
        sessions,
        { kind: "approve", sessionId, reason },
        actor,
      );
      setSessions(result.sessions);
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: result.audit.action,
          sessionId: result.audit.sessionId,
          reason: result.audit.reason,
          metadata: { ...result.audit.metadata, entityType: session.entityType },
        }),
      );
      // Only surface a contractor patch when the invite was bound to a
      // real contractor row (NOT the synthetic `onboarding:*` namespace).
      const cid = session.contractorId;
      const patch =
        cid && !cid.startsWith("onboarding:")
          ? { contractorId: cid, vettingStatus: "approved" as const }
          : null;
      return { sessionId, contractorPatch: patch };
    },
    [sessions, complianceDocuments],
  );

  const doRejectOnboarding = useCallback(
    (sessionId: string, actor: string, reason: string) => {
      assertNotObserving("rejectOnboarding");
      runLifecycle({ kind: "reject", sessionId, reason }, actor);
    },
    [runLifecycle],
  );

  const doArchiveOnboardingEntry = useCallback(
    async (
      ref: { sessionId?: string; tokenId?: string },
      actor: string,
      reason: string,
    ) => {
      assertNotObserving("archiveOnboardingEntry");
      const cleanReason = reason.trim() || "Archived from onboarding queue";
      const at = new Date().toISOString();

      if (ref.sessionId) {
        const session = sessions.find((s) => s.id === ref.sessionId);
        if (!session) {
          throw new Error(`OnboardingProvider: session ${ref.sessionId} not found.`);
        }
        setSessions((prev) =>
          prev.map((s) =>
            s.id === ref.sessionId
              ? {
                  ...s,
                  draft: {
                    ...s.draft,
                    archivedAt: s.draft.archivedAt ?? at,
                    archivedBy: actor,
                    archiveReason: cleanReason,
                    deletedAt: undefined,
                    deletedBy: undefined,
                    deleteReason: undefined,
                  },
                  lastTouchedAt: at,
                }
              : s,
          ),
        );
        setAuditEvents((prev) =>
          appendAuditEvent(prev, {
            actor,
            action: "draft_updated",
            sessionId: ref.sessionId,
            reason: cleanReason,
            metadata: { archived: true },
          }),
        );
        return deactivateConnectedOnboardingAccess({
          contractorIds: [session.contractorId].filter(Boolean) as string[],
          profileEmails: [session.draft?.contactEmail],
        });
      }

      if (!ref.tokenId) {
        throw new Error("OnboardingProvider: archive requires a sessionId or tokenId.");
      }
      const token = inviteTokens.find((t) => t.id === ref.tokenId);
      if (!token) {
        throw new Error(`OnboardingProvider: token ${ref.tokenId} not found.`);
      }
      setInviteTokens((prev) =>
        prev.map((t) =>
          t.id === ref.tokenId
            ? {
                ...t,
                status: "revoked",
                revokedAt: t.revokedAt ?? at,
                revokedBy: actor,
                revokedReason: `[archived] ${cleanReason}`,
              }
            : t,
        ),
      );
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "invite_revoked",
          tokenId: ref.tokenId,
          reason: cleanReason,
          metadata: { archived: true },
        }),
      );
      return deactivateConnectedOnboardingAccess({
        contractorIds: [token.contractorId].filter(Boolean) as string[],
        profileEmails: [],
      });
    },
    [inviteTokens, sessions],
  );

  const doDeleteOnboardingEntry = useCallback(
    async (
      ref: { sessionId?: string; tokenId?: string },
      actor: string,
      reason: string,
    ) => {
      assertNotObserving("deleteOnboardingEntry");
      const cleanReason = reason.trim() || "Deleted from onboarding queue";
      const at = new Date().toISOString();

      if (ref.sessionId) {
        const session = sessions.find((s) => s.id === ref.sessionId);
        if (!session) {
          throw new Error(`OnboardingProvider: session ${ref.sessionId} not found.`);
        }
        setSessions((prev) =>
          prev.map((s) =>
            s.id === ref.sessionId
              ? {
                  ...s,
                  draft: {
                    ...s.draft,
                    deletedAt: s.draft.deletedAt ?? at,
                    deletedBy: actor,
                    deleteReason: cleanReason,
                  },
                  lastTouchedAt: at,
                }
              : s,
          ),
        );
        setAuditEvents((prev) =>
          appendAuditEvent(prev, {
            actor,
            action: "draft_updated",
            sessionId: ref.sessionId,
            reason: cleanReason,
            metadata: { deleted: true },
          }),
        );
        return deactivateConnectedOnboardingAccess({
          contractorIds: [session.contractorId].filter(Boolean) as string[],
          profileEmails: [session.draft?.contactEmail],
        });
      }

      if (!ref.tokenId) {
        throw new Error("OnboardingProvider: delete requires a sessionId or tokenId.");
      }
      const token = inviteTokens.find((t) => t.id === ref.tokenId);
      if (!token) {
        throw new Error(`OnboardingProvider: token ${ref.tokenId} not found.`);
      }
      setInviteTokens((prev) =>
        prev.map((t) =>
          t.id === ref.tokenId
            ? {
                ...t,
                status: "revoked",
                revokedAt: t.revokedAt ?? at,
                revokedBy: actor,
                revokedReason: `[deleted] ${cleanReason}`,
              }
            : t,
        ),
      );
      setAuditEvents((prev) =>
        appendAuditEvent(prev, {
          actor,
          action: "invite_revoked",
          tokenId: ref.tokenId,
          reason: cleanReason,
          metadata: { deleted: true },
        }),
      );
      return deactivateConnectedOnboardingAccess({
        contractorIds: [token.contractorId].filter(Boolean) as string[],
        profileEmails: [],
      });
    },
    [inviteTokens, sessions],
  );


  const value = useMemo<OnboardingContextValue>(
    () => ({
      inviteTokens,
      sessions,
      auditEvents,
      complianceDocuments,
      createInviteToken: doCreateInviteToken,
      revokeInviteToken: doRevokeInviteToken,
      consumeInviteToken: doConsumeInviteToken,
      updateOnboardingDraft: doUpdateOnboardingDraft,
      completeOnboardingStep: doCompleteOnboardingStep,
      submitOnboardingSession: doSubmitOnboardingSession,
      requestReupload: doRequestReupload,
      submitComplianceDocument: doSubmitComplianceDocument,
      verifyComplianceDocument: doVerifyComplianceDocument,
      rejectComplianceDocument: doRejectComplianceDocument,
      submitOnboardingDocument: doSubmitOnboardingDocument,
      signOnboardingAgreement: doSignOnboardingAgreement,
      setOnboardingPayout: doSetOnboardingPayout,
      getOnboardingSubmitReadiness: doGetOnboardingSubmitReadiness,
      finalizeOnboardingSubmit: doFinalizeOnboardingSubmit,
      getAdminApprovalReadiness: doGetAdminApprovalReadiness,
      approveOnboarding: doApproveOnboarding,
      rejectOnboarding: doRejectOnboarding,
      archiveOnboardingEntry: doArchiveOnboardingEntry,
      deleteOnboardingEntry: doDeleteOnboardingEntry,
      requestOnboardingReupload: doRequestReupload,
    }),
    [
      inviteTokens,
      sessions,
      auditEvents,
      complianceDocuments,
      doCreateInviteToken,
      doRevokeInviteToken,
      doConsumeInviteToken,
      doUpdateOnboardingDraft,
      doCompleteOnboardingStep,
      doSubmitOnboardingSession,
      doRequestReupload,
      doSubmitComplianceDocument,
      doVerifyComplianceDocument,
      doRejectComplianceDocument,
      doSubmitOnboardingDocument,
      doSignOnboardingAgreement,
      doSetOnboardingPayout,
      doGetOnboardingSubmitReadiness,
      doFinalizeOnboardingSubmit,
      doGetAdminApprovalReadiness,
      doApproveOnboarding,
      doRejectOnboarding,
      doArchiveOnboardingEntry,
      doDeleteOnboardingEntry,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      <OnboardingSaveStatusContext.Provider value={saveStatus}>
        {children}
      </OnboardingSaveStatusContext.Provider>
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used inside OnboardingProvider.");
  }
  return ctx;
}
